/**
 * Drives the Subscribe & Save selector inside the product form.
 *
 * Appstle does not just render a widget — its app embed monkey-patches `window.fetch` and
 * rewrites the `/cart/add.js` body from its own widget state on the way out:
 *
 *   at window.fetch      (appstle-subscription.min.js)
 *   at #processAddToCart (product-form.js)
 *
 * So posting our own `selling_plan` cannot work while Appstle is installed — whatever the
 * theme sends, Appstle overwrites it. Hiding or disabling its inputs doesn't help either,
 * because the interceptor reads its widget state, not the form.
 *
 * The selector therefore *drives* Appstle instead of competing with it: picking an offer
 * checks the matching radio in Appstle's (visually hidden) widget and fires the events its
 * script listens for, so Appstle's own state — and the body it writes — is correct. Our
 * hidden input is disabled whenever Appstle is present so only one `selling_plan` is sent.
 *
 * With Appstle absent (uninstalled, or the embed turned off) there's no interceptor, so the
 * hidden input is used directly and everything still works.
 */

const APPSTLE_WIDGET = '.appstle_sub_widget, [id^="appstle_subscription_widget"]';

class LpProductOffers extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.addEventListener('change', this.#handleChange);
    this.#sync();

    // Appstle renders after us and re-renders on variant changes, so keep re-syncing.
    const form = this.closest('form');
    if (form) {
      this.#observer = new MutationObserver(() => this.#sync());
      this.#observer.observe(form, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (!event.target.matches('[data-lp-offer], [data-lp-offer-frequency]')) return;

    this.#sync();
  };

  /** The selling plan id the shopper has chosen, or '' for a one-time purchase. */
  get #selectedPlan() {
    const selected = this.querySelector('[data-lp-offer]:checked');
    const plan = selected instanceof HTMLInputElement ? selected.value : '';

    // With more than one selling plan the radio only says "subscribe" — the frequency
    // select decides which plan that means.
    const frequency = this.querySelector('[data-lp-offer-frequency]');
    return plan && frequency instanceof HTMLSelectElement ? frequency.value : plan;
  }

  /** Appstle's own selling-plan radios, if its widget is on the page. */
  get #appstleRadios() {
    const form = this.closest('form');
    if (!form) return [];

    return [...form.querySelectorAll('input[type="radio"][name="selling_plan"]')].filter(
      (radio) => !radio.closest('lp-product-offers')
    );
  }

  #sync() {
    const input = this.querySelector('[data-lp-offer-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const plan = this.#selectedPlan;
    const appstleRadios = this.#appstleRadios;
    const target = appstleRadios.find((radio) => radio.value === plan);

    if (target) {
      // Appstle is in charge of the outgoing request — hand it our selection and stay out
      // of the form so we don't post a second, conflicting selling_plan.
      input.disabled = true;
      for (const radio of appstleRadios) radio.disabled = false;

      if (!target.checked) {
        target.click();
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    // No Appstle widget (or no radio matching this plan) — post the value ourselves.
    input.disabled = false;
    input.value = plan;

    for (const radio of appstleRadios) radio.disabled = true;
  }
}

if (!customElements.get('lp-product-offers')) {
  customElements.define('lp-product-offers', LpProductOffers);
}
